import React, { useEffect, useState } from 'react';
import { X, Check, Shield, Zap, Sparkles, MessageCircle, AlertTriangle, Layers, Building, Users, Phone } from 'lucide-react';
import { fetchWithAuth } from '@/services/api';
import { toast } from 'react-hot-toast';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMessage?: string;
}

interface PlanUsage {
  plano: string;
  plano_expira_em?: string | null;
  trial_ativo?: boolean;
  limites: {
    lojas: number;
    usuarios: number;
    conexoes: number;
    catalogo: boolean;
  };
  uso: {
    lojas: number;
    usuarios: number;
    conexoes: number;
  };
}

const PLAN_CARDS = [
  {
    id: 'grátis',
    nome: 'Grátis',
    preco: 'R$ 00',
    periodo: '/mês',
    cor: 'border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/40',
    badgeCor: 'bg-gray-200 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
    recursos: [
      { label: '1 Loja', checked: true },
      { label: '1 Usuário', checked: true },
      { label: '1 Conexão WhatsApp', checked: true },
      { label: 'Catálogo de Produtos', checked: false }
    ]
  },
  {
    id: 'starter',
    nome: 'Starter',
    preco: 'R$ 00',
    periodo: '/mês',
    cor: 'border-blue-200 dark:border-blue-900 bg-blue-50/30 dark:bg-blue-950/20',
    badgeCor: 'bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300',
    recursos: [
      { label: '1 Loja', checked: true },
      { label: '2 Usuários', checked: true },
      { label: '1 Conexão WhatsApp', checked: true },
      { label: 'Catálogo de Produtos', checked: true }
    ]
  },
  {
    id: 'pro',
    nome: 'Pro',
    preco: 'R$ 00',
    periodo: '/mês',
    popular: true,
    cor: 'border-emerald-500 dark:border-emerald-500 bg-emerald-50/40 dark:bg-emerald-950/30 shadow-md',
    badgeCor: 'bg-emerald-600 text-white',
    recursos: [
      { label: '3 Lojas', checked: true },
      { label: '5 Usuários', checked: true },
      { label: '3 Conexões WhatsApp', checked: true },
      { label: 'Catálogo de Produtos', checked: true },
      { label: 'Relatórios Avançados', checked: true }
    ]
  },
  {
    id: 'personalizado',
    nome: 'Personalizado',
    preco: 'R$ 00',
    periodo: '',
    cor: 'border-purple-200 dark:border-purple-900 bg-purple-50/30 dark:bg-purple-950/20',
    badgeCor: 'bg-purple-100 text-purple-800 dark:bg-purple-900/60 dark:text-purple-300',
    recursos: [
      { label: 'Lojas Ilimitadas', checked: true },
      { label: 'Usuários Ilimitados', checked: true },
      { label: 'Conexões Ilimitadas', checked: true },
      { label: 'Catálogo & Relatórios', checked: true },
      { label: 'Suporte VIP 24/7', checked: true }
    ]
  }
];

export function UpgradeModal({ isOpen, onClose, initialMessage }: UpgradeModalProps) {
  const [usage, setUsage] = useState<PlanUsage | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [customMessage, setCustomMessage] = useState<string | null>(initialMessage || null);

  useEffect(() => {
    if (initialMessage) {
      setCustomMessage(initialMessage);
    }
  }, [initialMessage]);

  useEffect(() => {
    if (isOpen) {
      setLoadingUsage(true);
      fetchWithAuth('/api/tenant/plan-usage')
        .then(res => res.json())
        .then(data => {
          if (data && !data.error) {
            setUsage(data);
          }
        })
        .catch(err => console.error('[UPGRADE_MODAL] Erro ao buscar plano:', err))
        .finally(() => setLoadingUsage(false));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const currentPlanName = usage?.plano?.toLowerCase() || 'grátis';

  const handleContactSupport = (planName?: string) => {
    const text = encodeURIComponent(
      `Olá! Gostaria de fazer um upgrade no Task CRM para o plano ${planName || 'superior'}. Podemos conversar?`
    );
    window.open(`https://wa.me/5511999999999?text=${text}`, '_blank');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200 overflow-y-auto">
      <div className="bg-white dark:bg-[#1A1D21] rounded-2xl max-w-4xl w-full border border-gray-200 dark:border-gray-800 shadow-2xl overflow-hidden my-8 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-start justify-between bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-[#111B21] dark:to-[#1A1D21]">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="p-1.5 bg-blue-600 text-white rounded-lg">
                <Zap size={18} />
              </span>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Fazer Upgrade do Plano
              </h2>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Escolha o plano ideal para expandir suas lojas, equipe e conexões de WhatsApp no Task CRM.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-white rounded-lg transition-colors cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Limit Warning Banner if present */}
        {customMessage && (
          <div className="mx-6 mt-6 p-4 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 rounded-xl flex items-center gap-3 text-rose-800 dark:text-rose-300 text-xs sm:text-sm font-medium">
            <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0" />
            <div className="flex-1">
              <span className="font-bold">Ação bloqueada pelo limite do plano:</span> {customMessage}
            </div>
          </div>
        )}

        {/* Current Usage Bar */}
        {usage && (
          <div className="mx-6 mt-4 p-4 bg-gray-50 dark:bg-[#111B21] border border-gray-200 dark:border-gray-800 rounded-xl">
            <div className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center justify-between">
              <span>Seu Uso Atual (Plano {usage.plano.toUpperCase()})</span>
              {usage.plano_expira_em && (
                <span className="text-gray-500 font-normal">
                  Expira em: {new Date(usage.plano_expira_em).toLocaleDateString('pt-BR')}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div className="bg-white dark:bg-[#1A1D21] p-2.5 rounded-lg border border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 mb-1">
                  <Building size={13} />
                  <span>Lojas</span>
                </div>
                <div className="font-bold text-gray-900 dark:text-white text-sm">
                  {usage.uso.lojas} / {usage.limites.lojas === 999 ? '∞' : usage.limites.lojas}
                </div>
              </div>

              <div className="bg-white dark:bg-[#1A1D21] p-2.5 rounded-lg border border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 mb-1">
                  <Users size={13} />
                  <span>Usuários</span>
                </div>
                <div className="font-bold text-gray-900 dark:text-white text-sm">
                  {usage.uso.usuarios} / {usage.limites.usuarios === 999 ? '∞' : usage.limites.usuarios}
                </div>
              </div>

              <div className="bg-white dark:bg-[#1A1D21] p-2.5 rounded-lg border border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 mb-1">
                  <Phone size={13} />
                  <span>WhatsApp</span>
                </div>
                <div className="font-bold text-gray-900 dark:text-white text-sm">
                  {usage.uso.conexoes} / {usage.limites.conexoes === 999 ? '∞' : usage.limites.conexoes}
                </div>
              </div>

              <div className="bg-white dark:bg-[#1A1D21] p-2.5 rounded-lg border border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 mb-1">
                  <Layers size={13} />
                  <span>Catálogo</span>
                </div>
                <div className={`font-bold text-sm ${usage.limites.catalogo ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}>
                  {usage.limites.catalogo ? 'Ativo' : 'Indisponível'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PLAN_CARDS.map(plan => {
              const isCurrent = currentPlanName === plan.id;
              return (
                <div
                  key={plan.id}
                  className={`relative p-5 rounded-2xl border flex flex-col justify-between transition-all ${plan.cor}`}
                >
                  {plan.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wider shadow-sm flex items-center gap-1">
                      <Sparkles size={11} />
                      Mais Popular
                    </span>
                  )}

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-base text-gray-900 dark:text-white">{plan.nome}</h3>
                      {isCurrent && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-600 text-white">
                          Atual
                        </span>
                      )}
                    </div>

                    <div className="mb-4">
                      <span className="text-2xl font-extrabold text-gray-900 dark:text-white">{plan.preco}</span>
                      <span className="text-xs text-gray-500 font-medium">{plan.periodo}</span>
                    </div>

                    <ul className="space-y-2 mb-6 text-xs">
                      {plan.recursos.map((rec, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <span className={`p-0.5 rounded-full ${rec.checked ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400' : 'bg-gray-100 text-gray-400 dark:bg-gray-800'}`}>
                            {rec.checked ? <Check size={12} /> : <X size={12} />}
                          </span>
                          <span className={rec.checked ? 'text-gray-700 dark:text-gray-300 font-medium' : 'text-gray-400 line-through'}>
                            {rec.label}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    onClick={() => handleContactSupport(plan.nome)}
                    disabled={isCurrent}
                    className={`w-full py-2.5 px-4 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                      isCurrent
                        ? 'bg-gray-200 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                        : plan.popular
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    <MessageCircle size={14} />
                    {isCurrent ? 'Plano Atual' : 'Solicitar Upgrade'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 dark:bg-[#111B21] border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-1.5">
            <Shield size={14} className="text-emerald-600" />
            <span>Processamento manual e sem cobrança automática.</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 font-bold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white cursor-pointer"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}